'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import Link from 'next/link';

// ── Context ───────────────────────────────────────────────────────────────────

interface AuthGateContextValue {
  triggerAuthGate: () => void;
}

const AuthGateContext = createContext<AuthGateContextValue | null>(null);

// ── Icon ──────────────────────────────────────────────────────────────────────

function SeedlingIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 28V14" />
      <path d="M16 14C12 14 5 12 5 5C5 0.5 9 0 13 3C15 5 16 10 16 14Z" />
      <path d="M16 21C20 21 27 19 27 12C27 7.5 23 7 19 10C17 12 16 17 16 21Z" />
    </svg>
  );
}

// ── Provider (contains the modal) ─────────────────────────────────────────────

export function AuthGateProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  function triggerAuthGate() { setOpen(true); }
  function close() { setOpen(false); }

  return (
    <AuthGateContext.Provider value={{ triggerAuthGate }}>
      {children}

      {open && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-50"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={close}
          />

          {/* Card */}
          <div
            className="fixed z-[51] bg-white rounded-2xl shadow-xl
                        px-6 py-7 sm:px-8 sm:py-9
                        flex flex-col items-center"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'calc(100% - 48px)',
              maxWidth: '420px',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Icon */}
            <span className="text-deep-green mb-4">
              <SeedlingIcon />
            </span>

            {/* Heading */}
            <h2 className="font-heading text-[22px] text-dark-text text-center leading-snug mb-3">
              Save your garden progress
            </h2>

            {/* Subtext */}
            <p className="font-body text-[14px] text-sage text-center leading-relaxed max-w-[320px] mb-7">
              Create a free account to save journal entries, favourites, tasks, and garden
              plans across all your devices.
            </p>

            {/* Primary button */}
            <Link
              href="/register"
              onClick={close}
              className="block w-full text-center font-body text-[15px] font-semibold text-white
                         bg-deep-green py-3.5 rounded-lg hover:opacity-90 transition-opacity mb-3"
            >
              Create Free Account
            </Link>

            {/* Secondary button */}
            <Link
              href="/login"
              onClick={close}
              className="block w-full text-center font-body text-[15px] font-semibold text-deep-green
                         border-[1.5px] border-deep-green py-3.5 rounded-lg
                         hover:bg-light-sage transition-colors mb-5"
            >
              Sign In
            </Link>

            {/* Dismiss */}
            <button
              onClick={close}
              className="block font-body text-[13px] text-sage hover:text-dark-text
                         transition-colors text-center"
            >
              Just browsing
            </button>
          </div>
        </>
      )}
    </AuthGateContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuthGate(): AuthGateContextValue {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error('useAuthGate must be used inside <AuthGateProvider>');
  return ctx;
}
